import {
  IField,
  IList,
  IListColumnInternalList,
  IListElement,
} from 'app/services/api5-service/api.interface';

import { ISPFormState } from 'common/dynamic-form/model';
import { filterNotDefinedItems } from 'utils/filter-not-defined-items';

import { getRowDefaultButton } from './get-default-button';
import { getListCell } from './get-list-cell';
import { separateListColumns } from './separate-list-columns';

import { IListCellUi } from '../list-cell/model';
import { IListRowUi, ListConfig } from '../model';

/**
 * Append empty cells at the edge of the row for internal-list, according to UX preferences
 *
 * @param originalRowCells
 * @param internalListCell
 * @param internalListColumn - column metadata
 */
function getAdjustedInternalListCells(
  originalRowCells: IListCellUi[],
  internalListCell: IListCellUi,
  internalListColumn: IListColumnInternalList,
): IListCellUi[] {
  const emptyCellsBefore = Number(internalListColumn.$col_offset_left) || 0;

  const emptyCellsAfter = Number(internalListColumn.$col_offset_right) || 0;
  internalListCell.colspan =
    originalRowCells.length - emptyCellsBefore - emptyCellsAfter;

  return [
    ...Array.from({ length: emptyCellsBefore }).map(
      () => ({ type: 'empty', colspan: 1 } as IListCellUi),
    ),
    internalListCell,
    ...Array.from({ length: emptyCellsAfter }).map(
      () => ({ type: 'empty', colspan: 1 } as IListCellUi),
    ),
  ];
}

/**
 * Get internal list row
 *
 * @param originalRowCells - original row cells array
 * @param control - list metadata
 * @param field
 * @param internalListColumn
 * @param element
 * @param state
 * @param config - list config
 */
function getInternalListRow(
  originalRowCells: IListCellUi[],
  control: IList,
  field: IField,
  internalListColumn: IListColumnInternalList | undefined,
  element: IListElement,
  state: ISPFormState,
  config: ListConfig,
): IListRowUi | undefined {
  if (!internalListColumn) {
    return;
  }

  const rowId = element[control.$key]?.$;

  const parentCellsForInternalList = getListCell(
    control,
    field,
    internalListColumn,
    element,
    state,
    config,
    rowId,
  ).flat();
  if (parentCellsForInternalList.length === 0) {
    return;
  }
  const internalListCell = parentCellsForInternalList[0];

  // empty internal-list case. Not all rows can have data
  if (!internalListCell.listData) {
    return;
  }

  const internalListCells = getAdjustedInternalListCells(
    originalRowCells,
    internalListCell,
    internalListColumn,
  );

  return {
    keyValue: rowId,
    cells: internalListCells,
  } as IListRowUi;
}

/**
 * Add gap rows to rows list, if needed
 *
 * @param control - list meta
 * @param rows - list rows
 */
function getGappedRows(control: IList, rows: IListRowUi[]): IListRowUi[] {
  if (control.$row_type !== 'separated') {
    return rows;
  }

  const rowsWithGaps: IListRowUi[] = [];
  rows.forEach((row, index) => {
    const isRowEnd = row.part === 'end' || row.part === 'full';
    const isNotLast = index < rows.length - 1;
    rowsWithGaps.push(row);
    if (isRowEnd && isNotLast) {
      rowsWithGaps.push({
        cells: [],
        preset: 'gap',
      } as IListRowUi);
    }
  });

  return rowsWithGaps;
}

/**
 * Map list meta elements to list ui rows
 *
 * @param control - list metadata
 * @param field - field metadata
 * @param elements - list elements
 * @param state - dynamic form state
 * @param config - list config
 */
export function getListRowsUi(
  control: IList,
  field: IField,
  elements: IListElement[],
  state: ISPFormState,
  config: ListConfig,
): IListRowUi[] {
  const { simpleColumns, internalLists } = separateListColumns(control.col);

  const selectedRowKeys: string[] =
    state.docSubject.value[control.$name]?.$.split(',') || [];
  const rows = elements
    .map(element => {
      const keyValue = element[control.$key]?.$;

      const cells = simpleColumns
        .map(column =>
          getListCell(control, field, column, element, state, config, keyValue),
        )
        .flat();

      const row: IListRowUi = {
        key: control.$key,
        keyValue,
        originalElement: element,
        button: getRowDefaultButton(element, state),
        cells,
        selected: keyValue !== undefined && selectedRowKeys.includes(keyValue),
        disabled: element.$disabled === 'yes',
        background: element.$background,
        preset: control.$row_type || 'large',
      };

      const rowsWithInternalLists = filterNotDefinedItems(
        internalLists.map((internalListColumn, index) => {
          const rowWithInternalList = getInternalListRow(
            cells,
            control,
            field,
            internalListColumn,
            element,
            state,
            config,
          );

          if (rowWithInternalList) {
            rowWithInternalList.preset = control.$row_type || 'large';

            const isLast = index === internalLists.length - 1;
            rowWithInternalList.part = isLast ? 'end' : 'middle';
          }

          return rowWithInternalList;
        }),
      );

      row.part = rowsWithInternalLists.length ? 'start' : 'full';

      return [row, ...rowsWithInternalLists];
    })
    .flat();

  return getGappedRows(control, rows);
}
